import { auth } from '../config/firebase';

export interface TokenInfo {
  token: string;
  expiresAt: number;
  issuedAt: number;
}

const TOKEN_KEY = 'salesguru_jwt_token';
const TOKEN_INFO_KEY = 'salesguru_token_info';

// Get ID token from Firebase
export const getIdToken = async (forceRefresh = false): Promise<TokenInfo | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('[TokenUtils] No authenticated user');
      return null;
    }

    const token = await user.getIdToken(forceRefresh);
    const tokenResult = await user.getIdTokenResult(forceRefresh);
    
    const tokenInfo: TokenInfo = {
      token,
      expiresAt: new Date(tokenResult.expirationTime).getTime(),
      issuedAt: new Date(tokenResult.issuedAtTime).getTime()
    };

    console.debug('[TokenUtils] Token obtained:', {
      expiresAt: new Date(tokenInfo.expiresAt).toISOString(),
      tokenLength: token.length
    });

    return tokenInfo;
  } catch (error) {
    console.error('[TokenUtils] Error getting ID token:', error);
    return null;
  }
};

// Force refresh token
export const getIdTokenForced = async (): Promise<TokenInfo | null> => {
  return getIdToken(true);
};

// Store token in localStorage
export const storeToken = (tokenInfo: TokenInfo): void => {
  try {
    localStorage.setItem(TOKEN_KEY, tokenInfo.token);
    localStorage.setItem(TOKEN_INFO_KEY, JSON.stringify({
      expiresAt: tokenInfo.expiresAt,
      issuedAt: tokenInfo.issuedAt
    }));
    console.debug('[TokenUtils] Token stored in localStorage');
  } catch (error) {
    console.error('[TokenUtils] Error storing token:', error);
  }
};

// Get stored token from localStorage
export const getStoredToken = (): TokenInfo | null => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const tokenInfoStr = localStorage.getItem(TOKEN_INFO_KEY);
    
    if (!token || !tokenInfoStr) {
      return null;
    }

    const tokenInfo = JSON.parse(tokenInfoStr);
    
    // Check if token is expired
    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    
    if (now >= (tokenInfo.expiresAt - bufferTime)) {
      console.debug('[TokenUtils] Stored token is expired or expiring soon');
      clearStoredToken();
      return null;
    }

    return {
      token,
      expiresAt: tokenInfo.expiresAt,
      issuedAt: tokenInfo.issuedAt
    };
  } catch (error) {
    console.error('[TokenUtils] Error getting stored token:', error);
    clearStoredToken();
    return null;
  }
};

// Clear stored token
export const clearStoredToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_INFO_KEY);
    console.debug('[TokenUtils] Token cleared from localStorage');
  } catch (error) {
    console.error('[TokenUtils] Error clearing token:', error);
  }
};

// Validate and refresh token if needed
export const validateAndRefreshToken = async (): Promise<{ isValid: boolean; token: string | null }> => {
  try {
    // First check stored token
    const storedToken = getStoredToken();
    
    if (storedToken) {
      console.debug('[TokenUtils] Valid stored token found');
      return { isValid: true, token: storedToken.token };
    }

    // Try to get fresh token from Firebase
    const freshToken = await getIdToken(true);
    
    if (freshToken) {
      storeToken(freshToken);
      console.debug('[TokenUtils] Fresh token obtained and stored');
      return { isValid: true, token: freshToken.token };
    }

    console.warn('[TokenUtils] No valid token available');
    return { isValid: false, token: null };
  } catch (error) {
    console.error('[TokenUtils] Error validating/refreshing token:', error);
    return { isValid: false, token: null };
  }
};